/**
 * The single boundary for data sent to Google Analytics.
 *
 * Query strings are intentionally removed wholesale. Several of this site's
 * query parameters contain user-provided wheel data (`shared`, `chain`, and
 * `class`), and treating unknown future parameters as safe would make the
 * protection easy to regress. Hashes are omitted as well.
 */
export const GA_MEASUREMENT_ID = 'G-7HF5L8QJ3B';

type AnalyticsValue = string | number | boolean;
type AnalyticsParams = Record<string, AnalyticsValue>;

interface AnalyticsWindow extends Window {
  gtag?: (...args: unknown[]) => void;
  dataLayer?: unknown[];
  __tnwSanitizeAnalyticsUrl?: (value: string) => string;
}

let lastNavigationKey: string | null = null;
let currentPageLocation: string | null = null;
let currentPageReferrer: string | null = null;

export function sanitizeAnalyticsUrl(value: string): string {
  if (!value) return '';

  if (typeof window !== 'undefined') {
    const sharedSanitizer = (window as AnalyticsWindow).__tnwSanitizeAnalyticsUrl;
    if (sharedSanitizer) return sharedSanitizer(value);
  }

  try {
    const url = new URL(value, 'https://thenamewheel.com/');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch {
    return '';
  }
}

function analyticsWindow(): AnalyticsWindow | null {
  return typeof window === 'undefined' ? null : (window as AnalyticsWindow);
}

function send(...args: unknown[]): void {
  const w = analyticsWindow();
  if (!w) return;
  if (typeof w.gtag === 'function') {
    w.gtag(...args);
    return;
  }
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(args);
}

function pageContext(): { page_location: string; page_referrer: string } | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return {
    page_location: currentPageLocation ?? sanitizeAnalyticsUrl(window.location.href),
    page_referrer: currentPageReferrer ?? sanitizeAnalyticsUrl(document.referrer),
  };
}

/**
 * Record one manual page view for a React Router navigation.
 *
 * React StrictMode replays effects in development, so the immediately previous
 * navigation key is ignored. Older keys are not retained: returning with the
 * Back button is a new intended view and must be counted.
 */
export function trackPageView(navigationKey: string): void {
  if (lastNavigationKey === navigationKey || typeof window === 'undefined') return;

  const nextLocation = sanitizeAnalyticsUrl(window.location.href);
  const nextReferrer = currentPageLocation ?? sanitizeAnalyticsUrl(document.referrer);
  lastNavigationKey = navigationKey;
  currentPageLocation = nextLocation;
  currentPageReferrer = nextReferrer;

  const context = { page_location: nextLocation, page_referrer: nextReferrer };
  // Keep Enhanced Measurement events (scroll, static outbound links, etc.) on
  // the same scrubbed page context after a client-side route transition.
  send('set', context);
  send('event', 'page_view', {
    ...context,
    page_path: window.location.pathname,
    page_title: document.title,
    send_to: GA_MEASUREMENT_ID,
  });
}

/** Send a custom event with the same scrubbed page context as page views. */
export function trackAnalyticsEvent(name: string, params: AnalyticsParams = {}): void {
  const context = pageContext();
  if (!context) return;
  send('event', name, {
    ...params,
    ...context,
    send_to: GA_MEASUREMENT_ID,
  });
}
